import React, { useState, useEffect, useMemo } from 'react';
import { getCalendarDays, calculateCalendarStatus, formatWorkHours, getWorkingHoursSummary, getLeaveStatistics } from '../utils/attendanceHelper';

const AttendanceCalendar = ({ attendanceRecords, leaveRecords, holidays = [], isMobile, targetUserCreatedAt, targetUserName, onMonthChange, onDateClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSmallScreen = Boolean(isMobile) || windowWidth <= 768;
  const isGlobal = targetUserName === "Global Company";

  const daysInMonth = getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth());
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newDate);
    if (onMonthChange) onMonthChange(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newDate);
    if (onMonthChange) onMonthChange(newDate);
  };

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === currentMonth.getFullYear() && today.getMonth() === currentMonth.getMonth();

  const setToday = () => {
    const newDate = new Date();
    setCurrentMonth(newDate);
    if (onMonthChange) onMonthChange(newDate);
  };

  // Helper to render individual day cell
  const renderDayCell = (date, index) => {
    if (!date) {
      return (
        <div 
          key={`empty-${index}`} 
          className="calendar-day rounded-3" 
          style={{ minHeight: isSmallScreen ? "75px" : "100px", backgroundColor: "rgba(248, 250, 252, 0.5)" }}
        ></div>
      );
    }

    let { status, minutes, isHalfDay, name: holidayName, leave } = calculateCalendarStatus(date, attendanceRecords, leaveRecords, targetUserCreatedAt, holidays);
    
    if (isGlobal && status !== 'HOLIDAY' && status !== 'WEEKEND') {
      status = 'FUTURE'; // Hides absent/present status and acts as an empty date block
    }

    // Check if this date is today
    const isToday = date.getFullYear() === today.getFullYear() && 
                    date.getMonth() === today.getMonth() && 
                    date.getDate() === today.getDate();

    let bgColor = "#ffffff";
    let statusText = "";
    let badgeColor = "";
    let badgeBg = "";
    let badgeBorder = "";
    let textColor = "#334155";

    switch (status) {
      case 'WORKING':
        bgColor = "rgba(224, 242, 254, 0.5)";
        statusText = "Working";
        badgeColor = "#0284c7";
        badgeBg = "rgba(14, 165, 233, 0.15)";
        badgeBorder = "rgba(14, 165, 233, 0.3)";
        break;
      case 'MISSING_LOGOUT':
        bgColor = "rgba(255, 237, 213, 0.5)";
        statusText = "Missing Logout";
        badgeColor = "#c2410c";
        badgeBg = "rgba(249, 115, 22, 0.15)";
        badgeBorder = "rgba(249, 115, 22, 0.3)";
        break;
      case 'HOLIDAY':
        bgColor = "rgba(254, 240, 138, 0.4)"; // Soft gold/yellow
        statusText = holidayName || "Holiday";
        badgeColor = "#b45309"; // amber-700
        badgeBg = "rgba(251, 191, 36, 0.25)";
        badgeBorder = "rgba(251, 191, 36, 0.5)";
        break;
      case 'PRESENT':
        bgColor = "rgba(220, 252, 231, 0.4)"; // Soft green
        
        if (leave && leave.leaveType === 'Half Day') {
          statusText = "Half Day Leave";
          badgeColor = "#15803d"; // green-700
          badgeBg = "rgba(34, 197, 94, 0.15)";
          badgeBorder = "rgba(34, 197, 94, 0.3)";
        } else {
          statusText = isHalfDay ? "Incomplete" : (date.getDay() === 6 ? "Half Day" : "Present");
          if (isHalfDay) {
            badgeColor = "#b45309"; // amber-700
            badgeBg = "rgba(245, 158, 11, 0.15)";
            badgeBorder = "rgba(245, 158, 11, 0.3)";
          } else {
            badgeColor = "#15803d"; // green-700
            badgeBg = "rgba(34, 197, 94, 0.15)";
            badgeBorder = "rgba(34, 197, 94, 0.3)";
          }
        }
        break;
      case 'LEAVE':
        bgColor = "rgba(219, 234, 254, 0.5)"; // Soft blue
        statusText = leave?.leaveType === 'Half Day' ? "Half Day Leave" : "On Leave";
        badgeColor = "#1d4ed8"; // blue-700
        badgeBg = "rgba(59, 130, 246, 0.15)";
        badgeBorder = "rgba(59, 130, 246, 0.3)";
        break;
      case 'ABSENT':
        bgColor = "rgba(254, 226, 226, 0.5)"; // Soft red
        statusText = "Absent";
        badgeColor = "#b91c1c"; // red-700
        badgeBg = "rgba(239, 68, 68, 0.15)";
        badgeBorder = "rgba(239, 68, 68, 0.3)";
        break;
      case 'WEEKEND':
        bgColor = "#f8fafc"; // Slate-50
        statusText = "Weekend";
        textColor = "#94a3b8";
        break;
      case 'FUTURE':
      case 'WORKING_DAY':
        bgColor = "#ffffff";
        break;
      default:
        break;
    }

    return (
      <div 
        key={date.toISOString()} 
        className="calendar-day rounded-3 p-2 d-flex flex-column transition-all" 
        onClick={() => onDateClick && onDateClick(date, status, holidayName)}
        title={holidayName || statusText || undefined}
        style={{ 
          minHeight: isSmallScreen ? "75px" : "100px", 
          backgroundColor: bgColor,
          border: isToday ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid #f1f5f9',
          boxShadow: isToday ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
          position: "relative",
          transition: "all 0.2s ease",
          cursor: onDateClick ? "pointer" : "default"
        }}
        onMouseEnter={(e) => {
           e.currentTarget.style.transform = "translateY(-2px)";
           e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.06)";
           e.currentTarget.style.borderColor = "#e2e8f0";
           e.currentTarget.style.zIndex = "10";
        }}
        onMouseLeave={(e) => {
           e.currentTarget.style.transform = "none";
           e.currentTarget.style.boxShadow = isToday ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none';
           e.currentTarget.style.borderColor = isToday ? 'rgba(59, 130, 246, 0.5)' : '#f1f5f9';
           e.currentTarget.style.zIndex = "1";
        }}
      >
        <div className="d-flex justify-content-between align-items-start w-100 mb-1">
          <div 
            className={`d-flex align-items-center justify-content-center fw-semibold ${isToday ? 'bg-primary text-white shadow-sm' : ''}`}
            style={{ 
              width: isSmallScreen ? "24px" : "28px", 
              height: isSmallScreen ? "24px" : "28px", 
              borderRadius: "50%",
              fontSize: isSmallScreen ? "12px" : "13px",
              color: isToday ? "#fff" : textColor
            }}
          >
            {date.getDate()}
          </div>
          
          {status === 'PRESENT' && minutes > 0 && (
            <div className="fw-medium mt-1" style={{ fontSize: isSmallScreen ? "10px" : "11px", color: "#64748b", letterSpacing: "0.2px" }}>
              {formatWorkHours(minutes)}
            </div>
          )}
        </div>
        
        {statusText && (
          <div className="mt-auto pt-1 w-100 d-flex justify-content-center">
            {badgeColor ? (
              <span 
                className="badge rounded-pill fw-medium" 
                style={{ 
                  backgroundColor: badgeBg, 
                  color: badgeColor, 
                  border: `1px solid ${badgeBorder}`,
                  fontSize: isSmallScreen ? "9.5px" : "11px", 
                  padding: isSmallScreen ? "3px 6px" : "4px 10px", 
                  letterSpacing: "0.2px",
                  backdropFilter: "blur(4px)",
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden"
                }}
                title={statusText}
              >
                {statusText}
              </span>
            ) : (
              <span 
                className="text-center w-100" 
                style={{ 
                  fontSize: isSmallScreen ? "10px" : "11px", 
                  color: "#cbd5e1", 
                  fontStyle: "italic", 
                  letterSpacing: "0.3px",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
                title={statusText}
              >
                {statusText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const workStats = useMemo(() => {
    return getWorkingHoursSummary(attendanceRecords || [], null, currentMonth);
  }, [attendanceRecords, currentMonth]);

  const leaveStats = useMemo(() => {
    return getLeaveStatistics(targetUserCreatedAt, leaveRecords || []);
  }, [targetUserCreatedAt, leaveRecords]);

  return (
    <div className="bg-white rounded-4 shadow-sm p-2 p-md-4" style={{ border: "1px solid #f1f5f9" }}>
      {/* Calendar Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
        <h5 className="m-0 fw-bold d-flex align-items-center gap-2 gap-md-3" style={{ color: "#0f172a", letterSpacing: "-0.5px", fontSize: isSmallScreen ? "18px" : "20px" }}>
          <div className="d-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10" style={{ width: isSmallScreen ? "34px" : "40px", height: isSmallScreen ? "34px" : "40px" }}>
            <i className={`bi bi-calendar4-week text-primary ${isSmallScreen ? 'fs-6' : 'fs-5'}`}></i>
          </div>
          <span style={{ wordBreak: 'break-word' }}>
            {targetUserName ? `${targetUserName}'s Calendar` : 'Attendance Calendar'}
          </span>
        </h5>
        
        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
          <div className="d-flex align-items-center bg-white rounded-pill p-1 shadow-sm" style={{ border: "1px solid #e2e8f0" }}>
            <button 
              className="btn btn-sm btn-light rounded-pill d-flex align-items-center justify-content-center text-secondary transition-all" 
              onClick={prevMonth}
              style={{ width: "32px", height: "32px", border: "none", backgroundColor: "transparent" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              aria-label="Previous month"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            
            <div className="fw-semibold px-2 px-md-4 text-center" style={{ minWidth: isSmallScreen ? "120px" : "150px", color: "#1e293b", fontSize: isSmallScreen ? "14px" : "15px", letterSpacing: "0.3px" }}>
              {monthName}
            </div>

            <button 
              className="btn btn-sm btn-light rounded-pill d-flex align-items-center justify-content-center text-secondary transition-all" 
              onClick={nextMonth}
              style={{ width: "32px", height: "32px", border: "none", backgroundColor: "transparent" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              aria-label="Next month"
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          
          <button 
            className="btn btn-sm rounded-pill fw-medium px-3 px-md-4" 
            onClick={setToday}
            disabled={isCurrentMonth}
            style={{ 
              backgroundColor: isCurrentMonth ? "#f8fafc" : "#eff6ff",
              color: isCurrentMonth ? "#94a3b8" : "#2563eb",
              border: `1px solid ${isCurrentMonth ? '#e2e8f0' : '#bfdbfe'}`,
              transition: "all 0.2s",
              boxShadow: isCurrentMonth ? "none" : "0 2px 8px rgba(37, 99, 235, 0.15)",
              height: "36px"
            }}
          >
            Today
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      {!isGlobal && (
      <div className="row g-2 g-md-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 transition-all" style={{ backgroundColor: "#f8fafc" }}>
            <div className="card-body p-2 p-md-4 text-center">
              <div className="text-muted mb-1 mb-md-2 fw-semibold" style={{ fontSize: isSmallScreen ? "10px" : "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {isCurrentMonth ? "This Week Hours" : "Total Present Days"}
              </div>
              <h4 className="mb-0 fw-bold" style={{ color: "#0f172a", fontSize: isSmallScreen ? "16px" : "22px" }}>
                {isCurrentMonth 
                  ? formatWorkHours(workStats.thisWeekMinutes)
                  : <>{workStats.thisMonthPresentDays} <span style={{fontSize: isSmallScreen ? "11px" : "13px", fontWeight:"600", opacity: 0.7}}>Days</span></>}
              </h4>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 transition-all" style={{ backgroundColor: "#f8fafc" }}>
            <div className="card-body p-2 p-md-4 text-center">
              <div className="text-muted mb-1 mb-md-2 fw-semibold" style={{ fontSize: isSmallScreen ? "10px" : "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>This Month Hours</div>
              <h4 className="mb-0 fw-bold" style={{ color: "#0f172a", fontSize: isSmallScreen ? "16px" : "22px" }}>{formatWorkHours(workStats.thisMonthMinutes)}</h4>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 transition-all" style={{ backgroundColor: "#eff6ff", border: "1px solid #bfdbfe" }}>
            <div className="card-body p-2 p-md-4 text-center">
              <div className="mb-1 mb-md-2 fw-semibold" style={{ fontSize: isSmallScreen ? "10px" : "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "#3b82f6" }}>Leave Balance</div>
              <h4 className="mb-0 fw-bold" style={{ color: "#1d4ed8", fontSize: isSmallScreen ? "16px" : "22px" }}>{leaveStats.balance} <span style={{fontSize: isSmallScreen ? "11px" : "13px", fontWeight:"600", opacity: 0.7}}>Days</span></h4>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 transition-all" style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}>
            <div className="card-body p-2 p-md-4 text-center">
              <div className="mb-1 mb-md-2 fw-semibold" style={{ fontSize: isSmallScreen ? "10px" : "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "#ef4444" }}>Leaves Taken</div>
              <h4 className="mb-0 fw-bold d-flex align-items-center justify-content-center gap-1 flex-wrap" style={{ color: "#b91c1c", fontSize: isSmallScreen ? "16px" : "22px" }}>
                {leaveStats.totalTaken} 
                {leaveStats.carriedForward > 0 && (
                  <span className="badge rounded-pill bg-danger bg-opacity-25 text-danger ms-1" style={{ fontSize: "9px", padding: "3px 6px" }}>+{leaveStats.carriedForward}</span>
                )}
              </h4>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Mobile Swipe / Scroll Hint */}
      {isSmallScreen && (
        <div className="d-flex align-items-center justify-content-between text-muted mb-2 px-1" style={{ fontSize: "11px" }}>
          <span><i className="bi bi-arrows-expand me-1"></i> Swipe horizontally to view full week</span>
          {!isGlobal && <span>Includes weekly total</span>}
        </div>
      )}

      {/* Calendar Grid with Horizontal Scroll Container on Mobile */}
      <div 
        className="calendar-grid-wrapper mt-2"
        style={{
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: "8px"
        }}
      >
        <div 
          className="calendar-grid-inner"
          style={{
            minWidth: isGlobal ? (isSmallScreen ? "540px" : "100%") : (isSmallScreen ? "680px" : "100%"),
            width: "100%"
          }}
        >
          {/* Days of Week */}
          <div 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${isGlobal ? 7 : 8}, 1fr)`, 
              gap: isSmallScreen ? '6px' : '8px',
              marginBottom: '10px'
            }}
          >
            {(isGlobal ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Weekly']).map((day, idx) => (
              <div 
                key={day} 
                className="text-center fw-semibold py-2" 
                style={{ 
                  fontSize: isSmallScreen ? "11px" : "12px", 
                  color: idx === 0 || idx === 6 ? '#94a3b8' : (idx === 7 ? '#3b82f6' : '#64748b'), 
                  letterSpacing: "1px", 
                  textTransform: "uppercase" 
                }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Dates */}
          <div 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${isGlobal ? 7 : 8}, 1fr)`, 
              gap: isSmallScreen ? '6px' : '10px' 
            }}
          >
            {Array.from({ length: Math.ceil(daysInMonth.length / 7) }, (_, i) => daysInMonth.slice(i * 7, i * 7 + 7)).map((week, weekIdx) => {
              let weekMinutes = 0;
              week.forEach(date => {
                if (date) {
                  const { minutes } = calculateCalendarStatus(date, attendanceRecords, leaveRecords);
                  weekMinutes += (minutes || 0);
                }
              });

              return (
                <React.Fragment key={`week-${weekIdx}`}>
                  {week.map((date, idx) => (
                    <React.Fragment key={`day-${weekIdx}-${idx}`}>
                      {renderDayCell(date, (weekIdx * 7) + idx)}
                    </React.Fragment>
                  ))}
                  {/* 8th Column - Weekly Total */}
                  {!isGlobal && (
                  <div 
                    className="rounded-3 d-flex flex-column align-items-center justify-content-center transition-all p-1" 
                    style={{ 
                      minHeight: isSmallScreen ? "75px" : "100px", 
                      backgroundColor: "#f8fafc",
                      border: "1px dashed #cbd5e1",
                      boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)"
                    }}
                  >
                    <span style={{ fontSize: "9px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>Total</span>
                    <div className="fw-bold text-center" style={{ color: "#334155", fontSize: isSmallScreen ? "12px" : "14px" }}>
                      {formatWorkHours(weekMinutes)}
                    </div>
                  </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 mt-md-5 pt-3 pt-md-4 d-flex flex-wrap gap-3 gap-md-4 justify-content-center" style={{ borderTop: "1px solid #f1f5f9" }}>
        {[
          { label: "Holiday", color: "rgba(251, 191, 36, 0.25)", border: "rgba(251, 191, 36, 0.5)" },
          { label: "Present", color: "rgba(34, 197, 94, 0.15)", border: "rgba(34, 197, 94, 0.3)" },
          { label: "Leave", color: "rgba(59, 130, 246, 0.15)", border: "rgba(59, 130, 246, 0.3)" },
          { label: "Absent", color: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.3)" },
          { label: "Weekend", color: "#f8fafc", border: "#e2e8f0" },
        ].map(item => (
          <div key={item.label} className="d-flex align-items-center gap-1 gap-md-2">
            <div 
              className="rounded-circle shadow-sm"
              style={{ 
                width: isSmallScreen ? "10px" : "12px", 
                height: isSmallScreen ? "10px" : "12px", 
                backgroundColor: item.color, 
                border: `1px solid ${item.border}` 
              }}
            ></div>
            <span className="fw-medium" style={{ fontSize: isSmallScreen ? "11px" : "12px", color: "#64748b", letterSpacing: "0.2px" }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceCalendar;
